import React from "react"
import { cn } from "@/lib/utils"
import { LucideIcon } from "lucide-react"

interface AdminStatsCardProps {
  /**
   * Card title
   */
  title: string

  /**
   * Main value to display
   */
  value: string | number

  /**
   * Optional subtitle or additional info
   */
  subtitle?: string

  /**
   * Icon component
   */
  icon: LucideIcon

  /**
   * Trend indicator (positive, negative, or neutral)
   */
  trend?: "up" | "down" | "neutral"

  /**
   * Trend value (e.g., "+12%", "-5%")
   */
  trendValue?: string

  /**
   * Additional CSS classes
   */
  className?: string
}

export function AdminStatsCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
  className
}: AdminStatsCardProps) {
  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg p-6 space-y-3",
        className
      )}
    >
      {/* Header with icon */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <div className="p-2 bg-primary/10 rounded-md">
          <Icon className="w-4 h-4 text-primary" />
        </div>
      </div>

      {/* Main value */}
      <div className="space-y-1">
        <p className="text-3xl font-bold text-foreground">{value}</p>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>

      {/* Trend indicator */}
      {trend && trendValue && (
        <div className="flex items-center gap-1">
          <span
            className={cn(
              "text-xs font-medium",
              trend === "up" && "text-green-600 dark:text-green-500",
              trend === "down" && "text-red-600 dark:text-red-500",
              trend === "neutral" && "text-muted-foreground"
            )}
          >
            {trendValue}
          </span>
          <span className="text-xs text-muted-foreground">vs last week</span>
        </div>
      )}
    </div>
  )
}